import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {
  fetchTopRepositories,
  readRepositoryFromFragment,
  type RepositoryPickerRepository,
} from '@github-ui/item-picker/RepositoryPicker'
import {noop} from '@github-ui/noop'
import {issuePath} from '@github-ui/paths'
import {type RefObject, Suspense, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader, useRelayEnvironment} from 'react-relay'

import {VALUES} from '../constants/values'
import {IssueCreateContextProvider} from '../contexts/IssueCreateContext'
import type {CreateIssueProps} from '../CreateIssue'
import type {CreateIssueCallbackProps} from '../CreateIssueForm'
import type {TemplateListSharedProps} from '../TemplateList'
import {DisplayMode} from '../utils/display-mode'
import type {OnCreateProps} from '../utils/model'
import type {IssueCreateOptionConfig, SafeOptionConfig} from '../utils/option-config'
import {getSafeConfig} from '../utils/option-config'
import {constructIssueCreateParams} from '../utils/template-args'
import type {
  CreateIssueDialogEntryQuery,
  CreateIssueDialogEntryQuery$data,
} from './__generated__/CreateIssueDialogEntryQuery.graphql'
import {CreateIssueDialog} from './CreateIssueDialog'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const CreateIssueDialogEntryGraphQLQuery = graphql`
  query CreateIssueDialogEntryQuery($owner: String!, $name: String!, $includeTemplates: Boolean = false) {
    repository(owner: $owner, name: $name) {
      hasAnyTemplates
      isBlankIssuesEnabled
      hasIssuesEnabled
      ...RepositoryPickerRepository
      ...CreateIssueDialog @arguments(includeTemplates: $includeTemplates)
    }
  }
`

export const CreateIssueDialogEntry = ({...props}: CreateIssueDialogEntryProps): React.ReactElement | null => {
  return (
    <Suspense>
      <CreateIssueDialogEntryInternal {...props} />
    </Suspense>
  )
}

export type CreateIssueDialogEntryProps = {
  navigate: (url: string) => void
  isCreateDialogOpen: boolean
  setIsCreateDialogOpen: (value: boolean) => void
  optionConfig?: IssueCreateOptionConfig
  returnFocusRef?: RefObject<HTMLElement | null>
} & Partial<CreateIssueCallbackProps> &
  TemplateListSharedProps

export const CreateIssueDialogEntryInternal = ({
  navigate,
  isCreateDialogOpen,
  setIsCreateDialogOpen,
  onCancel,
  onCreateSuccess,
  onCreateError,
  optionConfig,
  isNavigatingToNew,
  setIsNavigatingToNew,
  returnFocusRef,
}: CreateIssueDialogEntryProps): React.ReactElement | null => {
  const environment = useRelayEnvironment()
  const [topRepositories, setTopRepositories] = useState<RepositoryPickerRepository[] | null>(null)
  const [currentRepoQueryRef, loadCurrentRepo, disposeCurrentRepo] = useQueryLoader<CreateIssueDialogEntryQuery>(
    CreateIssueDialogEntryGraphQLQuery,
  )

  const config = getSafeConfig(optionConfig)
  const shouldLoadTopRepos = config.showRepositoryPicker

  useEffect(() => {
    const fetchTopRepos = async () => {
      const repos = await fetchTopRepositories(environment, {
        first: VALUES.repositoriesPreloadCount,
        hasIssuesEnabled: true,
      })
      setTopRepositories(repos)
    }

    if (shouldLoadTopRepos) {
      fetchTopRepos()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Repository to prefill the create issue dialog with
   *
   * Note: The repo will only be determined based on the existence of an `owner` and `name` string,
   * without consideration for archived repos or other reasons the repository might not be available.
   */
  const prefillRepository = useMemo(() => {
    if (config.issueCreateArguments?.repository) {
      return {
        owner: config.issueCreateArguments.repository.owner,
        name: config.issueCreateArguments.repository.name,
      }
    }

    return null
  }, [config.issueCreateArguments?.repository])

  useEffect(() => {
    if (prefillRepository)
      loadCurrentRepo({...prefillRepository, includeTemplates: true}, {fetchPolicy: 'store-or-network'})

    return () => disposeCurrentRepo()
  }, [disposeCurrentRepo, prefillRepository, loadCurrentRepo])

  const handleDialogClose = () => {
    onCancel?.()
    setIsCreateDialogOpen(false)
  }

  const handleOnCreate = ({issue, createMore}: OnCreateProps) => {
    // Respect the callback if it exists, else use default behaviour.
    if (onCreateSuccess) {
      onCreateSuccess({issue, createMore})
    } else if (!createMore) {
      handleDialogClose()
      navigate(
        issuePath({
          owner: issue.repository.owner.login,
          repo: issue.repository.name,
          issueNumber: issue.number,
        }),
      )
    }
  }

  const handleOnError = (error: Error) => onCreateError?.(error)

  const createIssueDialogProps: CreateIssuePropsEntry = {
    topRepositories,
    onCreateSuccess: handleOnCreate,
    onCreateError: handleOnError,
    onCancel: handleDialogClose,
    navigate,
    isNavigatingToNew,
    setIsNavigatingToNew,
  }

  if (!isCreateDialogOpen) {
    return null
  }

  // If we are waiting for the top repos to load, or we are waiting for the current repo to load then don't render
  if ((shouldLoadTopRepos && !topRepositories) || (!currentRepoQueryRef && prefillRepository)) {
    return null
  }

  return (
    <CreateIssueEntry
      createIssueProps={createIssueDialogProps}
      config={config}
      currentRepoQueryRef={currentRepoQueryRef ?? null}
      returnFocusRef={returnFocusRef}
    />
  )
}

export type CreateIssuePropsEntry = Omit<CreateIssueProps, 'issueFormRef' | 'currentRepository'>

type CreateIssueEntryBaseProps = {
  createIssueProps: CreateIssuePropsEntry
  config: SafeOptionConfig
  returnFocusRef?: RefObject<HTMLElement | null>
}

export type CreateIssueEntryProps = CreateIssueEntryBaseProps & {
  currentRepoQueryRef: PreloadedQuery<CreateIssueDialogEntryQuery> | null
}

export type CreateIssueEntryPropsWithRepo = CreateIssueEntryBaseProps & {
  currentRepoQueryRef: PreloadedQuery<CreateIssueDialogEntryQuery>
}

type CreateIssueEntryInternal = CreateIssueEntryBaseProps & {
  currentRepoData: CreateIssueDialogEntryQuery$data | null
}

const CreateIssueEntry = ({currentRepoQueryRef, ...props}: CreateIssueEntryProps) => {
  if (currentRepoQueryRef) {
    return <CreateIssueEntryWithRepo currentRepoQueryRef={currentRepoQueryRef} {...props} />
  } else {
    return <CreateIssueEntryInternal currentRepoData={null} {...props} />
  }
}

export const CreateIssueEntryWithRepo = ({currentRepoQueryRef, ...props}: CreateIssueEntryPropsWithRepo) => {
  const data = usePreloadedQuery<CreateIssueDialogEntryQuery>(CreateIssueDialogEntryGraphQLQuery, currentRepoQueryRef)
  return <CreateIssueEntryInternal currentRepoData={data} {...props} />
}

// Since we have the current repository already loaded, we can now check if theres no templates available
// and if so, directly bypass the template selector.

const CreateIssueEntryInternal = ({
  createIssueProps,
  config,
  currentRepoData,
  returnFocusRef,
}: CreateIssueEntryInternal) => {
  const navigate = createIssueProps.navigate ?? noop
  const repository =
    currentRepoData && currentRepoData.repository ? readRepositoryFromFragment(currentRepoData.repository) : null
  const issueFormRef = useRef<IssueFormRef>(null)

  const preselectedData = {
    repository: repository ?? undefined,
    template: undefined,
    parentIssue: config.issueCreateArguments?.parentIssue,
  }

  const props = {
    ...createIssueProps,
    issueFormRef,
    hideCreateMore: config.hideCreateMore,
  }

  const repoMeta = currentRepoData?.repository
  let redirect = false
  const shouldEvaluateRedirect =
    repoMeta?.hasIssuesEnabled && config.defaultDisplayMode !== DisplayMode.IssueDuplication
  if (shouldEvaluateRedirect) {
    // Redirect when no templates AND blank issues are enabled
    // hasAnyTemplates is always true if there are any contact links for the repo
    redirect =
      navigate !== noop && !!repository && !!repoMeta && !repoMeta.hasAnyTemplates && repoMeta.isBlankIssuesEnabled
  }

  useEffect(() => {
    if (redirect && repository) {
      const searchParams = constructIssueCreateParams({
        includeRepository: false,
        repository,
        templateFileName: undefined,
        ...config.issueCreateArguments?.initialValues,
      })
      const url = `/${repository.nameWithOwner}/issues/new${searchParams ? `?${searchParams}` : ''}`
      navigate(url)
    }
  }, [navigate, redirect, repository, config.issueCreateArguments?.initialValues])

  if (redirect) {
    return null
  }

  return (
    <IssueCreateContextProvider
      optionConfig={config}
      overrideFallbackDisplaymode={DisplayMode.TemplatePicker}
      preselectedData={preselectedData}
    >
      <CreateIssueDialog currentRepository={currentRepoData?.repository} {...props} returnFocusRef={returnFocusRef} />
    </IssueCreateContextProvider>
  )
}
